import { isErrorWithData } from '@/services/helpers';
import {
  useCreateOrganizationWebhookMutation,
  useUpdateOrganizationWebhookMutation,
} from '@/services/organizations';
import {
  Box,
  Button,
  FormikSelectInputNative,
  FormikTextInput,
  FormikToggle,
  Modal,
  toast,
  useOpenClose,
} from '@palmetto/palmetto-components';
import { Field, Formik } from 'formik';
import { useMemo, useState } from 'react';
import { Form } from 'react-router-dom';
import * as yup from 'yup';

import { WebhookAuthenticationType } from 'types';

import { ApiKeyDisplayModal } from './ApiKeyDisplayModal';

const WebhookAuthenticationTypeOptions = [
  {
    label: 'Server API Key',
    value: WebhookAuthenticationType.apiKey,
    description: (
      <p>
        The default. Use a Palmetto provided API key, that will be generated on creation. Each request coming from
        Palmetto will have an <code>apiKey</code> header with this value.
      </p>
    ),
  },
  {
    label: 'Client Headers',
    value: WebhookAuthenticationType.clientHeaders,
    description: (
      <p>
        Provide a client ID and client secret. Each request coming from Palmetto will have <code>clientId</code>
        &nbsp;and <code>clientSecret</code> headers with these values.
      </p>
    ),
  },
  {
    label: 'Client Basic Authentication Header',
    value: WebhookAuthenticationType.basicAuthHeader,
    description: (
      <p>
        Similar to Client Headers, supply a client ID and client secret. Each request we send will have a Basic&nbsp;
        <code>Authorization</code> header consisting of the combined pair, <code>clientId:clientSecret</code>, base64
        encoded. See API docs for more details.
      </p>
    ),
  },
];

const getWebhookAuthenticationTypeDescription = (type: WebhookAuthenticationType) => {
  const option = WebhookAuthenticationTypeOptions.find((option) => option.value === type);
  return option ? option.description : null;
};

const isClientAuthenticationType = (type: WebhookAuthenticationType) => {
  return type === WebhookAuthenticationType.clientHeaders || type === WebhookAuthenticationType.basicAuthHeader;
};

/**
 * The credential requirement has to track the authentication block's own `!isEditing` render
 * condition. Editing never shows or sends these fields, and the API no longer returns them, so
 * requiring them there fails validation on two inputs the user cannot see or fill.
 */
const buildWebhookSchema = (isEditing: boolean) => {
  const requiredWhenCreatingWithClientAuth = {
    is: (value: WebhookAuthenticationType) => !isEditing && isClientAuthenticationType(value),
    then: (schema: yup.StringSchema) => schema.required('Required for client header or basic authentication.'),
    otherwise: (schema: yup.StringSchema) => schema.notRequired(),
  };

  return yup.object().shape({
    name: yup.string().required('This is a required field.'),
    hostUrl: yup.string().url().required('This is a required field.'),
    includeVisibleOrgEvents: yup.boolean().required('This is a required field.'),
    authentication: yup.string().required('This is a required field.'),
    clientId: yup.string().when('authentication', requiredWhenCreatingWithClientAuth),
    clientSecret: yup.string().when('authentication', requiredWhenCreatingWithClientAuth),
  });
};

const defaultValues: {
  name: string;
  hostUrl: string;
  includeVisibleOrgEvents: boolean;
  authentication: WebhookAuthenticationType;
} = {
  name: '',
  hostUrl: '',
  includeVisibleOrgEvents: false,
  authentication: WebhookAuthenticationType.apiKey,
};

export type WebhookSchema = yup.InferType<ReturnType<typeof buildWebhookSchema>>;
export const EditWebhookRegistrationModal = ({
  organizationAlias,
  registration,
  isOpen,
  handleClose,
}: {
  organizationAlias: string;
  registration?: any;
  isOpen: boolean;
  handleClose: () => void;
}) => {
  const [createWebhook] = useCreateOrganizationWebhookMutation();
  const [updateWebhook] = useUpdateOrganizationWebhookMutation();

  const [generatedApiKey, setGeneratedApiKey] = useState<string | null>(null);
  const {
    isOpen: isApiKeyModalOpen,
    handleClose: handleApiKeyModalClose,
    handleOpen: handleApiKeyModalOpen,
  } = useOpenClose();

  const isEditing = !!registration;

  const webhookSchema = useMemo(() => buildWebhookSchema(isEditing), [isEditing]);

  const existingValues = registration
    ? {
        name: registration.name,
        hostUrl: registration.hostUrl,
        includeVisibleOrgEvents: registration.includeVisibleOrgEvents,
        authentication: registration.authentication?.type ?? WebhookAuthenticationType.apiKey,
        clientId: '',
        clientSecret: '',
      }
    : null;

  const initialValues = existingValues ?? defaultValues;

  const handleSubmit = async (values: WebhookSchema, { resetForm }: any) => {
    try {
      if (isEditing) {
        // Listed by hand rather than spread from form state: that state carries blank clientId and
        // clientSecret placeholders, which updateWebhookClient rejects on a client-auth registration.
        const updateBody = {
          name: values.name,
          hostUrl: values.hostUrl,
          includeVisibleOrgEvents: values.includeVisibleOrgEvents,
        };
        await updateWebhook({
          registrationId: registration.id,
          body: updateBody,
        }).unwrap();
        toast.success('Webhook updated');
        handleClose();
        resetForm();
      } else {
        const body: any = {
          name: values.name,
          hostUrl: values.hostUrl,
          includeVisibleOrgEvents: values.includeVisibleOrgEvents,
          authentication: values.authentication,
        };

        if (isClientAuthenticationType(values.authentication as WebhookAuthenticationType)) {
          body.clientId = values.clientId;
          body.clientSecret = values.clientSecret;
        }

        const result = await createWebhook({ alias: organizationAlias, body }).unwrap();
        handleClose();
        resetForm();

        // If an API key was generated, show it to the user
        if (result.apiKey) {
          setGeneratedApiKey(result.apiKey);
          handleApiKeyModalOpen();
        } else {
          toast.success('Webhook created');
        }
      }
    } catch (e) {
      if (isErrorWithData(e)) {
        toast.error(e.data.message);
      } else {
        console.error(e);
        toast.error(isEditing ? 'Error updating webhook' : 'Error creating webhook');
      }
    }
  };

  const handleApiKeyModalDismiss = () => {
    setGeneratedApiKey(null);
    handleApiKeyModalClose();
  };

  return (
    <>
      {generatedApiKey && (
        <ApiKeyDisplayModal
          apiKey={generatedApiKey}
          isOpen={isApiKeyModalOpen}
          handleClose={handleApiKeyModalDismiss}
        />
      )}
      <Formik
        validationSchema={webhookSchema}
        initialValues={initialValues}
        validateOnChange={false}
        onSubmit={handleSubmit}
        enableReinitialize={true}
      >
        {({ isSubmitting, resetForm, submitForm, values }) => {
          return (
            <Form noValidate>
              <Modal
                isOpen={isOpen}
                maxWidth="4xl"
                ariaLabelledBy="EditWebhookRegistrationModal"
                onDismiss={() => {
                  resetForm();
                  handleClose();
                }}
              >
                <Modal.Header
                  id="EditWebhookRegistrationModal"
                  title="Webhook Registration"
                  onDismiss={() => {
                    resetForm();
                    handleClose();
                  }}
                />
                <Modal.Body background="secondary" childGap="lg">
                  <Box childGap={{ base: 'lg', desktop: 'xl' }}>
                    <Field type="text" label="Name" name="name" id="name" component={FormikTextInput} isRequired />

                    <Field
                      type="url"
                      label="Base URL"
                      name="hostUrl"
                      id="hostUrl"
                      component={FormikTextInput}
                      isRequired
                      placeholder="https://example.com"
                    />

                    <Field
                      type="toggle"
                      label="Include Visible Org Events"
                      name="includeVisibleOrgEvents"
                      id="includeVisibleOrgEvents"
                      component={FormikToggle}
                    />
                    {!isEditing && (
                      <Box childGap="sm">
                        <Field
                          id="authentication"
                          name="authentication"
                          label="Authentication"
                          component={FormikSelectInputNative}
                          options={WebhookAuthenticationTypeOptions}
                          isRequired
                        />
                        <Box fontSize="sm" margin="sm 0">
                          {getWebhookAuthenticationTypeDescription(
                            values.authentication as WebhookAuthenticationType,
                          )}
                        </Box>
                        {isClientAuthenticationType(values.authentication as WebhookAuthenticationType) && (
                          <>
                            <Field
                              type="text"
                              label="Client ID"
                              name="clientId"
                              id="clientId"
                              component={FormikTextInput}
                              isRequired
                            />
                            <Field
                              type="text"
                              label="Client Secret"
                              name="clientSecret"
                              id="clientSecret"
                              component={FormikTextInput}
                              isRequired
                            />
                          </>
                        )}
                      </Box>
                    )}
                  </Box>
                </Modal.Body>
                <Modal.Footer>
                  <Button
                    variant="secondary"
                    tone="neutral"
                    isLoading={isSubmitting}
                    isDisabled={isSubmitting}
                    onClick={() => {
                      resetForm();
                      handleClose();
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    isLoading={isSubmitting}
                    isDisabled={isSubmitting}
                    type="submit"
                    onClick={submitForm}
                  >
                    Save
                  </Button>
                </Modal.Footer>
              </Modal>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};
